import React from 'react';
import { ReactComponent as WorkerRegistrationIcon } from 'assets/icons/WorkerRegistration.svg';
import { ReactComponent as TakePhotoIcon } from 'assets/icons/TakePhoto.svg';
import { ReactComponent as PersonalInformationIcon } from 'assets/icons/PersonalInformation.svg';
import { ReactComponent as ContactInformationIcon } from 'assets/icons/ContactInformation.svg';
import { ReactComponent as TradesIcon } from 'assets/icons/Trades.svg';
import { ReactComponent as CertificationsIcon } from 'assets/icons/Certifications.svg';
import { ReactComponent as ConsentFormIcon } from 'assets/icons/ConsentForm.svg';
import { ROUTES } from 'constants/routes';
import type { RootState } from '../../../state-mgmt/store';
import { WorkerProfileField } from 'types/profileCustomFields';
import { WorkerInvitationStatus } from 'models/general';
import { PERSONAL_INFO_FIELD_CONFIG } from 'hooks/usePersonalInformationFields';
import { CONTACT_INFO_FIELD_CONFIG } from 'hooks/useContactInformationFields';
import { TRADES_FIELD_CONFIG } from '../../pages/Trades/hooks/useTradesFields';

// Helper function to check if all fields in a configuration are not visible
const areAllFieldsNotVisible = (fieldConfig: Partial<Record<WorkerProfileField, any>>, state: RootState): boolean => {
  const { profileFieldConstraintsMap } = state.cache;

  const fieldCodes = Object.keys(fieldConfig) as WorkerProfileField[];

  // If all fields have no constraint or are not visible, section should be hidden
  return fieldCodes.every(fieldCode => {
    const constraint = profileFieldConstraintsMap[fieldCode];
    // Not visible if no constraint exists or if isVisible is false
    return !constraint || !constraint.isVisible;
  });
};

// Helper functions to check if all fields in a section are not visible
export const areAllPersonalInfoFieldsNotVisible = (state: RootState): boolean => {
  return areAllFieldsNotVisible(PERSONAL_INFO_FIELD_CONFIG, state);
};

export const areAllContactInfoFieldsNotVisible = (state: RootState): boolean => {
  return areAllFieldsNotVisible(CONTACT_INFO_FIELD_CONFIG, state);
};

export const areAllTradesFieldsNotVisible = (state: RootState): boolean => {
  return areAllFieldsNotVisible(TRADES_FIELD_CONFIG, state);
};

// Wizard step configuration interface
export interface WizardStepConfig {
  key: string;
  titleKey?: string;
  defaultTitle: string;
  icon: () => React.ReactNode;
  route: string;
  pathSegment: string;
  subSteps?: WizardSubStepConfig[];
  shouldSkip?: (state: RootState) => boolean; // Function to determine if step should be skipped
}

// Sub-step configuration interface
export interface WizardSubStepConfig {
  key: string;
  titleKey?: string;
  defaultTitle: string;
  route: string;
  pathSegment: string;
  shouldSkip?: (state: RootState) => boolean; // Function to determine if sub-step should be skipped
}

// Create a stepkey enum for type safety. Make the keys in all caps
export enum StepKey {
  REGISTRATION = 'registration',
  TAKE_PHOTO = 'takePhoto',
  PERSONAL_INFORMATION = 'personalInformation',
  CONTACT_INFORMATION = 'contactInformation',
  TRADES = 'trades',
  CERTIFICATIONS = 'certifications',
  CONSENT_FORM = 'consentForm',
  TERMS_AND_CONDITIONS = 'registrationTerms',
  STANDARD_CONSENT_FORM = 'registrationConsentForm',
  STANDARD_BIOMETRIC_CONSENT = 'registrationBiometricConsent',
  REGISTRATION_SIGNATURE = 'registrationSignature',
}

// Centralized wizard steps configuration
export const WIZARD_STEPS: WizardStepConfig[] = [
  {
    key: StepKey.REGISTRATION,
    titleKey: 'basicInformation.title',
    defaultTitle: 'Registration',
    icon: () => <WorkerRegistrationIcon />,
    route: ROUTES.BASIC_INFORMATION.path,
    pathSegment: 'registration',
    subSteps: [
      {
        key: 'basicInformation',
        titleKey: 'basicInformation.title',
        defaultTitle: 'Basic Information',
        route: ROUTES.BASIC_INFORMATION.path,
        pathSegment: 'basic-information',
      },
      {
        key: 'registrationDetails',
        titleKey: 'workerRegistration.welcome',
        defaultTitle: 'Registration Details',
        route: ROUTES.REGISTRATION.path,
        pathSegment: 'registration',
      },
      {
        key: 'createPassword',
        titleKey: 'createPassword.title',
        defaultTitle: 'Create Password',
        route: ROUTES.CREATE_PASSWORD.path,
        pathSegment: 'create-password',
      },
      {
        key: StepKey.TERMS_AND_CONDITIONS,
        defaultTitle: 'Terms & Conditions',
        route: ROUTES.TERMS_AND_CONDITIONS.path,
        pathSegment: 'terms-and-conditions',
      },
      {
        key: StepKey.STANDARD_CONSENT_FORM,
        defaultTitle: 'Standard Project Consent Form',
        route: ROUTES.STANDARD_CONSENT_FORM.path,
        pathSegment: 'standard-consent-form',
      },
      {
        key: StepKey.STANDARD_BIOMETRIC_CONSENT,
        defaultTitle: 'Biometric Consent',
        route: ROUTES.STANDARD_BIOMETRIC_CONSENT.path,
        pathSegment: 'standard-biometric-consent',
      },
      {
        key: StepKey.REGISTRATION_SIGNATURE,
        defaultTitle: 'Signature',
        route: ROUTES.REGISTRATION_SIGNATURE.path,
        pathSegment: 'registration-signature',
      },
    ],
  },
  {
    key: StepKey.TAKE_PHOTO,
    defaultTitle: 'Take a Photo',
    icon: () => <TakePhotoIcon />,
    route: ROUTES.TAKE_PHOTO.path,
    pathSegment: 'take-photo',
  },
  {
    key: StepKey.PERSONAL_INFORMATION,
    defaultTitle: 'Personal Information',
    icon: () => <PersonalInformationIcon />,
    route: ROUTES.PERSONAL_INFORMATION.path,
    pathSegment: 'personal-information',
    shouldSkip: areAllPersonalInfoFieldsNotVisible,
  },
  {
    key: StepKey.CONTACT_INFORMATION,
    defaultTitle: 'Contact Information',
    icon: () => <ContactInformationIcon />,
    route: ROUTES.CONTACT_INFORMATION.path,
    pathSegment: 'contact-information',
    shouldSkip: areAllContactInfoFieldsNotVisible,
  },
  {
    key: StepKey.TRADES,
    defaultTitle: 'Trades',
    icon: () => <TradesIcon />,
    route: ROUTES.TRADES.path,
    pathSegment: 'trades',
    shouldSkip: areAllTradesFieldsNotVisible,
  },
  {
    key: StepKey.CERTIFICATIONS,
    defaultTitle: 'Certifications',
    icon: () => <CertificationsIcon />,
    route: ROUTES.CERTIFICATIONS?.path,
    pathSegment: 'certifications',
  },
  {
    key: StepKey.CONSENT_FORM,
    defaultTitle: 'Consent Form',
    icon: () => <ConsentFormIcon />,
    route: ROUTES.CONSENT_FORM?.path,
    pathSegment: 'consent-form',
    subSteps: [
      {
        key: 'consentForm',
        defaultTitle: 'Consent Form',
        route: ROUTES.CONSENT_FORM?.path,
        pathSegment: 'consent-form',
      },
      {
        key: 'consentFormInformation',
        defaultTitle: 'Consent Form Information',
        route: ROUTES.CONSENT_FORM_INFORMATION.path,
        pathSegment: 'consent-form-information',
      },
    ],
  },
];

// Essential utility functions for wizard navigation

// Whole-segment path match. A substring check (`pathname.includes(pathSegment)`) gives
// false positives when one segment is a prefix of another — e.g. `/registration-signature`
// contains `registration`, and `/consent-form-information` contains `consent-form` — which
// misidentifies the current step/sub-step and breaks Next/Back navigation. Matching on the
// `/`-delimited path segments avoids this. Both arguments are expected to be lowercase.
const pathMatchesSegment = (currentPath: string, pathSegment: string): boolean =>
  currentPath.split('/').filter(Boolean).includes(pathSegment);

export const getStepIndexFromPath = (pathname: string): number => {
  const currentPath = pathname.toLowerCase();

  // Find matching step by checking main steps and their sub-steps
  for (let i = 0; i < WIZARD_STEPS.length; i++) {
    const step = WIZARD_STEPS[i];

    // Check if the current path matches any sub-step
    if (step.subSteps) {
      const hasMatchingSubStep = step.subSteps.some(subStep =>
        pathMatchesSegment(currentPath, subStep.pathSegment.toLowerCase()),
      );
      if (hasMatchingSubStep) {
        return i;
      }
    }

    // Check if the current path matches the main step
    if (pathMatchesSegment(currentPath, step.pathSegment.toLowerCase())) {
      return i;
    }
  }

  // Return default to first step if no match found
  return 0;
};

export const getSubStepIndexFromPath = (pathname: string, stepIndex: number): number => {
  const currentPath = pathname.toLowerCase();
  const step = WIZARD_STEPS[stepIndex];

  if (!step?.subSteps) {
    return 0;
  }

  // Find matching sub-step
  const subStepIndex = step.subSteps.findIndex(subStep =>
    pathMatchesSegment(currentPath, subStep.pathSegment.toLowerCase()),
  );

  return subStepIndex !== -1 ? subStepIndex : 0;
};

export const getStepTitle = (stepConfig: WizardStepConfig, customTitle?: string, t?: any): string => {
  if (customTitle) return customTitle;
  if (stepConfig.titleKey && t) return t(stepConfig.titleKey);
  return stepConfig.defaultTitle;
};

export const isValidStepIndex = (index: number): boolean => {
  return index >= 0 && index < WIZARD_STEPS.length;
};

export const isValidSubStepIndex = (stepIndex: number, subStepIndex: number): boolean => {
  const step = WIZARD_STEPS[stepIndex];
  if (!step?.subSteps) return false;
  return subStepIndex >= 0 && subStepIndex < step.subSteps.length;
};

// Navigation utility functions
export const getNextStepIndex = (currentIndex: number): number | null => {
  const nextIndex = currentIndex + 1;
  return isValidStepIndex(nextIndex) ? nextIndex : null;
};

export const getPreviousStepIndex = (currentIndex: number): number | null => {
  const previousIndex = currentIndex - 1;
  return isValidStepIndex(previousIndex) ? previousIndex : null;
};

// New navigation functions for sub-steps
export const getNextSubStepIndex = (stepIndex: number, currentSubStepIndex: number): number | null => {
  const step = WIZARD_STEPS[stepIndex];
  if (!step?.subSteps) return null;

  const nextSubStepIndex = currentSubStepIndex + 1;
  return isValidSubStepIndex(stepIndex, nextSubStepIndex) ? nextSubStepIndex : null;
};

export const getPreviousSubStepIndex = (stepIndex: number, currentSubStepIndex: number): number | null => {
  const step = WIZARD_STEPS[stepIndex];
  if (!step?.subSteps) return null;

  const previousSubStepIndex = currentSubStepIndex - 1;
  return isValidSubStepIndex(stepIndex, previousSubStepIndex) ? previousSubStepIndex : null;
};

// Helper function to get the route for navigation
export const getRouteForNavigation = (stepIndex: number, subStepIndex?: number): string => {
  const step = WIZARD_STEPS[stepIndex];
  if (!step) return '/';

  if (step.subSteps && subStepIndex !== undefined && isValidSubStepIndex(stepIndex, subStepIndex)) {
    return step.subSteps[subStepIndex].route;
  }

  return step.route;
};
